/**
 * Measurements placed on a time axis, and grouped into periods.
 *
 * Every number a chart or a report shows is computed here rather than in a
 * component, so the app on screen and the PDF someone hands to a doctor cannot
 * disagree, and so the native app inherits the arithmetic rather than
 * reimplementing it (root `CLAUDE.md`). Only the drawing is platform-specific.
 *
 * Two rules run through the whole file:
 *
 *  - **An empty period is absent, never zero.** A week with no logging is not a
 *    week scoring zero. Buckets with no data simply do not exist in the output,
 *    and each bucket says whether the period before it was one of them, so a
 *    line can break rather than run straight across four missing days.
 *  - **An unordered enum has no numeric series.** That is enforced by the type:
 *    `Series` is a discriminated union, and only the quantitative branch
 *    carries quantities. There is no code path that averages
 *    `breakfast / lunch / dinner` because there is no value to average.
 */

import type { Entry } from '../model/entry.js'
import { measurementFor } from '../model/entry.js'
import type { Timestamp, WallClock } from '../model/timestamp.js'
import { dayKey, epochFromWallClock, wallClockOf } from '../model/timestamp.js'
import { describeValue, isMultiKind, isQuantitative, quantify } from '../model/track.js'
import type { Track, TrackValue } from '../model/track.js'
import type { EpochRange } from '../ports/storage.js'

const DAY_MS = 86_400_000

export interface SeriesPoint {
  readonly at: Timestamp
  readonly entryId: string
  readonly value: TrackValue
  /** Resolved through the track's own configuration — never a bare option id. */
  readonly label: string
}

export interface QuantitativePoint extends SeriesPoint {
  /** Position on the track's scale: the number, 0 or 1, or the option score. */
  readonly quantity: number
}

interface SeriesBase {
  readonly track: Track
  /**
   * Measurements that no longer resolve against the track.
   *
   * Counted rather than dropped in silence: a value that does not match the
   * track's current kind is a real event — an edit that got through, or an
   * import from an older schema — and a chart quietly showing fewer points than
   * the timeline is the kind of discrepancy nobody investigates because nobody
   * notices it.
   */
  readonly unresolved: number
}

export interface QuantitativeSeries extends SeriesBase {
  readonly kind: 'quantitative'
  readonly points: readonly QuantitativePoint[]
}

export interface CategoricalSeries extends SeriesBase {
  readonly kind: 'categorical'
  readonly points: readonly SeriesPoint[]
}

export type Series = QuantitativeSeries | CategoricalSeries

/**
 * Every measurement of one track, in order, optionally narrowed to a range.
 *
 * Entries with no measurement for this track contribute nothing — they are not
 * a zero and not a gap-filling placeholder, they simply did not measure it.
 */
export function buildSeries(
  track: Track,
  entries: readonly Entry[],
  range?: EpochRange,
): Series {
  const quantitative = isQuantitative(track.kind)
  const quantitativePoints: QuantitativePoint[] = []
  const categoricalPoints: SeriesPoint[] = []
  let unresolved = 0

  for (const entry of entries) {
    if (
      range !== undefined &&
      (entry.at.epochMs < range.fromEpochMs || entry.at.epochMs > range.toEpochMs)
    ) {
      continue
    }
    const measurement = measurementFor(entry, track.id)
    if (measurement === undefined) {
      continue
    }

    const label = describeValue(track, measurement.value)
    if (label === null) {
      unresolved += 1
      continue
    }
    const point: SeriesPoint = {
      at: entry.at,
      entryId: entry.id,
      value: measurement.value,
      label,
    }

    /*
     * A multi-select becomes one point per option chosen (T-1301).
     *
     * The alternative is one point carrying "Headache · Tired", which tallies as
     * a category of its own — so the same two symptoms recorded in one entry
     * would never be counted alongside the days they appeared separately, and
     * every distinct combination would get its own bar. Expanding here means
     * every consumer downstream counts options without knowing this kind exists.
     *
     * The points share an `entryId`, which is why anything counting *entries*
     * has to count distinct ids rather than points.
     */
    if (isMultiKind(track.kind) && Array.isArray(measurement.value)) {
      const chosen = new Set(measurement.value)
      for (const option of track.kind.options) {
        if (chosen.has(option.id)) {
          categoricalPoints.push({ ...point, value: option.id, label: option.label })
        }
      }
      continue
    }

    if (!quantitative) {
      categoricalPoints.push(point)
      continue
    }

    const quantity = quantify(track, measurement.value)
    if (quantity === null) {
      unresolved += 1
      continue
    }
    quantitativePoints.push({ ...point, quantity })
  }

  const byInstant = (a: SeriesPoint, b: SeriesPoint) => a.at.epochMs - b.at.epochMs

  return quantitative
    ? {
        kind: 'quantitative',
        track,
        unresolved,
        points: quantitativePoints.sort(byInstant),
      }
    : {
        kind: 'categorical',
        track,
        unresolved,
        points: categoricalPoints.sort(byInstant),
      }
}

// ─── Bucketing ────────────────────────────────────────────────────────────────

export type BucketSize = 'hour' | 'day' | 'week' | 'month'

export type Aggregate = 'mean' | 'median' | 'min' | 'max' | 'count' | 'sum'

export interface Bucket {
  /**
   * A calendar label, not an instant: `2026-07-28`, `2026-W31`, `2026-07`.
   *
   * Keys are how buckets are identified and compared, because a label is
   * zone-independent where an instant is not. Two entries recorded on the same
   * local day in different countries belong to the same day.
   */
  readonly key: string
  readonly startEpochMs: number
  /** Exclusive. A DST day is genuinely 23 or 25 hours long here. */
  readonly endEpochMs: number
  /**
   * The zone the boundaries were resolved in — the first point's.
   *
   * Boundaries are only used to place the bucket on an axis. Membership is
   * decided by `key`, in each entry's own captured zone, so an entry logged
   * abroad still lands on the day it happened.
   */
  readonly timeZone: string
}

interface Gapped {
  /**
   * Whether the immediately preceding period produced no bucket.
   *
   * The reason this is computed here rather than left to a renderer: deciding
   * it needs the calendar and the zone, and a chart that guesses from pixel
   * distance draws a straight line across a fortnight of missing data.
   */
  readonly followsGap: boolean
}

export interface AggregatedBucket extends Gapped {
  readonly bucket: Bucket
  readonly value: number
  readonly count: number
  readonly points: readonly QuantitativePoint[]
}

export interface CategoryCount {
  readonly optionId: string
  readonly label: string
  readonly count: number
}

export interface CategoryBucket extends Gapped {
  readonly bucket: Bucket
  readonly counts: readonly CategoryCount[]
  readonly total: number
  readonly points: readonly SeriesPoint[]
}

export interface BucketedQuantitativeSeries {
  readonly kind: 'quantitative'
  readonly track: Track
  readonly size: BucketSize
  readonly aggregate: Aggregate
  readonly buckets: readonly AggregatedBucket[]
}

export interface BucketedCategoricalSeries {
  readonly kind: 'categorical'
  readonly track: Track
  readonly size: BucketSize
  readonly buckets: readonly CategoryBucket[]
}

export type BucketedSeries = BucketedQuantitativeSeries | BucketedCategoricalSeries

export interface BucketOptions {
  readonly size: BucketSize
  /** Ignored for a categorical series, which counts categories instead. */
  readonly aggregate?: Aggregate
}

function pad(value: number, width = 2): string {
  return String(Math.abs(value)).padStart(width, '0')
}

/**
 * The ISO-8601 week label, e.g. `2026-W31`.
 *
 * The year is taken from the Thursday of the same week, which is what makes
 * 31 December 2025 fall in `2026-W01` rather than inventing a 53rd week.
 */
function isoWeekKey(wall: WallClock): string {
  const midnight = Date.UTC(wall.year, wall.month - 1, wall.day)
  const mondayIndex = (new Date(midnight).getUTCDay() + 6) % 7
  const thursday = midnight + (3 - mondayIndex) * DAY_MS
  const isoYear = new Date(thursday).getUTCFullYear()
  const firstThursday = Date.UTC(isoYear, 0, 4)
  const firstMonday =
    firstThursday - ((new Date(firstThursday).getUTCDay() + 6) % 7) * DAY_MS
  const week = Math.round((thursday - firstMonday) / (7 * DAY_MS)) + 1
  return `${pad(isoYear, 4)}-W${pad(week)}`
}

/** Which bucket an instant belongs to, decided in the zone it was captured in. */
export function bucketKeyOf(at: Timestamp, size: BucketSize): string {
  const wall = wallClockOf(at)
  switch (size) {
    case 'hour':
      return `${dayKey(at)}T${pad(wall.hour)}`
    case 'day':
      return dayKey(at)
    case 'week':
      return isoWeekKey(wall)
    case 'month':
      return `${pad(wall.year, 4)}-${pad(wall.month)}`
  }
}

/** The wall clock at which a bucket begins, given any reading inside it. */
function startWallClock(wall: WallClock, size: BucketSize): WallClock {
  const midnight = { ...wall, hour: 0, minute: 0, second: 0 }
  switch (size) {
    case 'hour':
      return { ...wall, minute: 0, second: 0 }
    case 'day':
      return midnight
    case 'week': {
      const mondayIndex =
        (new Date(Date.UTC(wall.year, wall.month - 1, wall.day)).getUTCDay() + 6) % 7
      return shiftDays(midnight, -mondayIndex)
    }
    case 'month':
      return { ...midnight, day: 1 }
  }
}

/** Calendar arithmetic on a reading, with no zone involved. */
function shiftDays(wall: WallClock, days: number): WallClock {
  const shifted = new Date(
    Date.UTC(
      wall.year,
      wall.month - 1,
      wall.day + days,
      wall.hour,
      wall.minute,
      wall.second,
    ),
  )
  return {
    year: shifted.getUTCFullYear(),
    month: shifted.getUTCMonth() + 1,
    day: shifted.getUTCDate(),
    hour: shifted.getUTCHours(),
    minute: shifted.getUTCMinutes(),
    second: shifted.getUTCSeconds(),
  }
}

/** The reading at which the following bucket begins. */
function nextWallClock(start: WallClock, size: BucketSize): WallClock {
  switch (size) {
    case 'hour': {
      const shifted = new Date(
        Date.UTC(start.year, start.month - 1, start.day, start.hour + 1, 0, 0),
      )
      return {
        year: shifted.getUTCFullYear(),
        month: shifted.getUTCMonth() + 1,
        day: shifted.getUTCDate(),
        hour: shifted.getUTCHours(),
        minute: 0,
        second: 0,
      }
    }
    case 'day':
      return shiftDays(start, 1)
    case 'week':
      return shiftDays(start, 7)
    case 'month': {
      const shifted = new Date(Date.UTC(start.year, start.month, 1))
      return {
        year: shifted.getUTCFullYear(),
        month: shifted.getUTCMonth() + 1,
        day: 1,
        hour: 0,
        minute: 0,
        second: 0,
      }
    }
  }
}

function bucketFor(at: Timestamp, size: BucketSize): Bucket {
  const zone = at.timeZone
  const start = startWallClock(wallClockOf(at), size)
  const startEpochMs = epochFromWallClock(start, zone)
  return {
    key: bucketKeyOf(at, size),
    startEpochMs,
    endEpochMs: epochFromWallClock(nextWallClock(start, size), zone),
    timeZone: zone,
  }
}

/**
 * The key of the period immediately before a bucket.
 *
 * Derived from the instant one millisecond before the bucket starts rather than
 * from string arithmetic on the key, which gets two cases wrong for free: a
 * 25-hour day has two 01:00 buckets, and a 23-hour day has no 01:00 bucket at
 * all. Stepping back through instants asks the calendar instead of assuming it.
 */
function previousKeyOf(bucket: Bucket, size: BucketSize): string {
  return bucketKeyOf(
    { epochMs: bucket.startEpochMs - 1, timeZone: bucket.timeZone },
    size,
  )
}

function median(sorted: readonly number[]): number {
  const middle = Math.floor(sorted.length / 2)
  if (sorted.length % 2 === 1) {
    return sorted[middle] ?? 0
  }
  return ((sorted[middle - 1] ?? 0) + (sorted[middle] ?? 0)) / 2
}

function aggregateOf(quantities: readonly number[], aggregate: Aggregate): number {
  switch (aggregate) {
    case 'count':
      return quantities.length
    case 'sum':
      return quantities.reduce((total, value) => total + value, 0)
    case 'mean':
      return quantities.reduce((total, value) => total + value, 0) / quantities.length
    case 'median':
      return median([...quantities].sort((a, b) => a - b))
    case 'min':
      return Math.min(...quantities)
    case 'max':
      return Math.max(...quantities)
  }
}

function groupByKey<P extends SeriesPoint>(
  points: readonly P[],
  size: BucketSize,
): { bucket: Bucket; points: P[] }[] {
  const groups = new Map<string, { bucket: Bucket; points: P[] }>()
  for (const point of points) {
    const key = bucketKeyOf(point.at, size)
    const existing = groups.get(key)
    if (existing === undefined) {
      groups.set(key, { bucket: bucketFor(point.at, size), points: [point] })
    } else {
      existing.points.push(point)
    }
  }
  return [...groups.values()].sort(
    (a, b) => a.bucket.startEpochMs - b.bucket.startEpochMs,
  )
}

/**
 * Groups a series into periods.
 *
 * Only periods containing measurements appear. Nothing is zero-filled, and the
 * caller is told where the holes are through `followsGap` rather than having to
 * reconstruct the calendar to find out.
 */
export function bucketSeries(series: Series, options: BucketOptions): BucketedSeries {
  const { size } = options

  if (series.kind === 'categorical') {
    const buckets = groupByKey(series.points, size).map((group, index, all) => {
      const counts = new Map<string, CategoryCount>()
      for (const point of group.points) {
        const optionId = String(point.value)
        const existing = counts.get(optionId)
        counts.set(optionId, {
          optionId,
          label: point.label,
          count: (existing?.count ?? 0) + 1,
        })
      }
      const previous = all[index - 1]
      return {
        bucket: group.bucket,
        counts: [...counts.values()].sort((a, b) => b.count - a.count),
        /*
         * Entries, not points. One multi-select entry contributes a point per
         * option chosen, so counting points would report "12 entries" for four
         * days of three symptoms — and the chart's own table is headed Entries.
         */
        total: new Set(group.points.map((point) => point.entryId)).size,
        points: group.points,
        followsGap:
          previous !== undefined &&
          previous.bucket.key !== previousKeyOf(group.bucket, size),
      }
    })
    return { kind: 'categorical', track: series.track, size, buckets }
  }

  const aggregate = options.aggregate ?? 'mean'
  const buckets = groupByKey(series.points, size).map((group, index, all) => {
    const previous = all[index - 1]
    return {
      bucket: group.bucket,
      value: aggregateOf(
        group.points.map((point) => point.quantity),
        aggregate,
      ),
      count: group.points.length,
      points: group.points,
      followsGap:
        previous !== undefined &&
        previous.bucket.key !== previousKeyOf(group.bucket, size),
    }
  })
  return { kind: 'quantitative', track: series.track, size, aggregate, buckets }
}

/**
 * A granularity that keeps a chart legible for the range being shown.
 *
 * Hourly buckets over a year is a quarter of a million columns; monthly buckets
 * over a week is one. The thresholds aim for somewhere between roughly ten and
 * a hundred buckets, which is what fits on a phone.
 */
export function defaultBucketSize(range: EpochRange): BucketSize {
  const days = (range.toEpochMs - range.fromEpochMs) / DAY_MS
  if (days <= 2) {
    return 'hour'
  }
  if (days <= 70) {
    return 'day'
  }
  if (days <= 400) {
    return 'week'
  }
  return 'month'
}
